import React, { useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';
import { useEditorContext } from '../../context/editor-context';

/**
 * Botón principal con gradiente (usado como "Generar video" en create,
 * "Guardar cambios" en edit). Extraído para poder reusar la misma UI con
 * distintos labels/callbacks.
 */
const PrimaryButton: React.FC<{
  label: string;
  labelBusy: string;
  onClick: () => void;
  disabled: boolean;
  busy: boolean;
}> = ({ label, labelBusy, onClick, disabled, busy }) => {
  const { spacing, fontSize } = useResponsiveSizes();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => !disabled && setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing.xs,
        padding: `${spacing.sm}px ${spacing.md}px`,
        borderRadius: 10,
        border: 'none',
        fontSize: fontSize.base,
        fontWeight: 500,
        color: disabled ? C.textDisabled : '#fff',
        background: disabled
          ? C.surface
          : hovered
            ? 'linear-gradient(135deg, #4A5A9D 0%, #B8329F 100%)'
            : 'linear-gradient(135deg, #333F83 0%, #9A218C 100%)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : '0 4px 12px rgba(154, 33, 140, 0.2)',
        transition: 'all 0.15s ease',
        transform: hovered && !disabled ? 'translateY(-1px)' : 'none',
        whiteSpace: 'nowrap'
      }}
    >
      {busy && <LoadingOutlined />}
      {busy ? labelBusy : label}
    </button>
  );
};

/**
 * Botón secundario outline (usado como "Guardar como nuevo" en edit mode).
 * Reservamos el treatment de gradiente para la acción primaria.
 */
const SecondaryButton: React.FC<{
  label: string;
  onClick: () => void;
  disabled: boolean;
}> = ({ label, onClick, disabled }) => {
  const { spacing, fontSize } = useResponsiveSizes();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => !disabled && setHovered(true)}
      onMouseLeave={() => !disabled && setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: spacing.xs,
        padding: `${spacing.sm}px ${spacing.md}px`,
        borderRadius: 10,
        border: `1px solid ${disabled ? C.border : C.accent}`,
        fontSize: fontSize.base,
        fontWeight: 500,
        color: disabled ? C.textDisabled : C.accent,
        background: disabled ? C.surface : hovered ? C.accentSurface : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );
};

export const GenerateBar: React.FC = () => {
  const { totalScenes, isProcessing, isValid, onGenerate, onUpdate, editingProjectId } = useEditorContext();
  const { spacing, fontSize } = useResponsiveSizes();
  const disabled = !isValid || isProcessing;
  const isEditMode = editingProjectId !== null;

  return (
    <div
      style={{
        flexShrink: 0,
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: `${spacing.md}px ${spacing.lg}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <div>
        <div style={{ fontSize: fontSize.lg, fontWeight: 600, color: C.textPrimary, marginBottom: 2 }}>
          {isEditMode ? 'Editando proyecto' : 'Listo para generar tu video'}
        </div>
        <div style={{ fontSize: fontSize.sm, color: C.textSecondary }}>
          {totalScenes} {totalScenes === 1 ? 'escena' : 'escenas'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
        {isProcessing && (
          <span style={{ fontSize: fontSize.md, color: C.textMuted, fontStyle: 'italic' }}>
            Aguarde unos minutos, el video se está generando
          </span>
        )}

        {isEditMode ? (
          <>
            <SecondaryButton label='Guardar como nuevo' onClick={onGenerate} disabled={disabled} />
            <PrimaryButton
              label='Guardar cambios'
              labelBusy='Guardando...'
              onClick={onUpdate}
              disabled={disabled}
              busy={isProcessing}
            />
          </>
        ) : (
          <PrimaryButton
            label='Generar video'
            labelBusy='Generando...'
            onClick={onGenerate}
            disabled={disabled}
            busy={isProcessing}
          />
        )}
      </div>
    </div>
  );
};
